import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  Order,
  OrderService,
} from '../../../features/live-orders/order.service';
import {
  distinctUntilChanged,
  map,
  Subscription,
  firstValueFrom,
  Observable,
  of,
  filter,
} from 'rxjs';
import { TenantService } from '../../../core/services/tenant.service';
import {
  SunmiPrinter,
  printReceiptWithFallback,
} from '../../../core/native/sunmi-printer';
import {
  buildReceiptLines,
  buildReceiptText,
} from '../../../core/printing/receipt-text';

type LocalStatus = Order['status'];

@Component({
  selector: 'app-order-details-dialog',
  standalone: false,
  templateUrl: './order-details-dialog.component.html',
  styleUrl: './order-details-dialog.component.scss',
})
export class OrderDetailsDialogComponent implements OnChanges, OnDestroy {
  @Input() orderId!: string | null;
  @Input() currentStatus?: LocalStatus;

  @Output() refundRequested = new EventEmitter<string>();

  loading = false;
  error: string | null = null;
  details: any = null;
  actionBusy = false;

  /** live status from the board stream (updates as actions fire) */
  private statusSub?: Subscription;
  liveStatus?: LocalStatus;

  constructor(
    private orderService: OrderService,
    private tenantService: TenantService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {
    this.tenantSub = this.tenantService.tenantConfig$
      .pipe(filter((cfg): cfg is any => !!cfg && !!cfg.tenant))
      .subscribe((cfg) =>
        this.updateViewState(() => {
          this.tenantName = cfg.tenant.companyName || 'Restaurant';
          this.companyNumber = cfg.tenant.companyNumber || null;
        }),
      );
  }

  tenantName = 'Restaurant';
  companyNumber: string | null = null;

  private tenantSub?: Subscription;
  private destroyed = false;

  ngOnChanges(changes: SimpleChanges) {
    if ('currentStatus' in changes) {
      this.liveStatus = this.currentStatus;
    }

    if ('orderId' in changes) {
      this.statusSub?.unsubscribe();
      this.liveStatus = this.currentStatus;

      if (this.orderId) {
        this.statusSub = this.orderService.orders$
          .pipe(
            map((list) => list.find((o) => o.id === this.orderId)?.status),
            distinctUntilChanged(),
          )
          .subscribe((s) =>
            this.updateViewState(() => {
              this.liveStatus = s;
            }),
          );

        this.fetch();
      } else {
        this.liveStatus = undefined;
        this.details = null;
      }
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.statusSub?.unsubscribe();
    this.tenantSub?.unsubscribe();
  }

  private updateViewState(update: () => void): void {
    this.zone.run(() => {
      update();
      this.scheduleViewRefresh();
    });
  }

  private scheduleViewRefresh(): void {
    Promise.resolve().then(() => {
      if (!this.destroyed) {
        this.cdr.detectChanges();
      }
    });
  }

  private fetch(force = false) {
    if (!this.orderId) return;
    if (this.loading) return;

    this.loading = true;
    this.error = null;
    this.details = null;
    this.scheduleViewRefresh();

    this.orderService.viewOrderDetails(this.orderId, force).subscribe({
      next: (d) => {
        this.updateViewState(() => {
          this.details = d;
          this.loading = false;
        });
      },
      error: () => {
        this.updateViewState(() => {
          this.error = 'Failed to load order details.';
          this.loading = false;
        });
      },
    });
  }

  private mapDetailsToLocalStatus(): LocalStatus | undefined {
    const s = String(this.details?.orderStatus || '').toLowerCase();
    if (!s) return undefined;
    if (s.includes('pending')) return 'incoming';
    if (s.includes('confirm')) return 'accepted';
    if (s.includes('cook')) return 'preparing';
    if (s.includes('deliver')) return 'delivery';
    if (s.includes('complete')) return 'completed';
    if (s.includes('cancel')) return 'declined';
    return undefined;
  }

  get effectiveStatus(): LocalStatus {
    return this.liveStatus ?? this.mapDetailsToLocalStatus() ?? 'accepted';
  }

  get showAcceptDecline() {
    return this.effectiveStatus === 'incoming';
  }
  get showStartPrep() {
    return this.effectiveStatus === 'accepted';
  }
  get showSendDelivery() {
    return this.effectiveStatus === 'preparing';
  }
  get showComplete() {
    return this.effectiveStatus === 'delivery';
  }

  get canRefund(): boolean {
    const paidLike = String(this.details?.paymentStatus).toLowerCase();
    const isPaid =
      paidLike === 'paid' ||
      paidLike === 'captured' ||
      paidLike === 'succeeded' ||
      paidLike === 'partial_refund';

    const refundable =
      Number(
        this.details?.refundableAmount ??
          this.details?.orderTotalCurrentGross ??
          0,
      ) > 0;

    // Optional guard: don’t show when order is still pending/incoming
    const notPending = this.effectiveStatus !== 'incoming';

    return (
      !!this.orderId && isPaid && refundable && notPending && !this.actionBusy
    );
  }

  /** NEW: fire event upward */
  onRefund() {
    if (this.orderId && this.canRefund) {
      this.refundRequested.emit(this.orderId);
    }
  }

  // --------- Actions ----------
  async acceptAndPrint() {
    if (!this.orderId) return;

    this.actionBusy = true;

    try {
      // 1) wait for backend success
      await firstValueFrom(this.orderService.acceptOrder(this.orderId));

      // 2) refetch details (recommended so receipt is accurate)
      this.orderService.invalidateOrderDetails(this.orderId);
      const latest = await firstValueFrom(
        this.orderService.viewOrderDetails(this.orderId, true),
      );
      if (latest) {
        this.updateViewState(() => {
          this.details = latest;
        });
      }

      // 3) print
      await this.printCurrentDetails();
    } catch (err: any) {
      console.error('Accept+Print failed', err);
      alert(err?.message ?? String(err));
    } finally {
      this.updateViewState(() => {
        this.actionBusy = false;
      });
    }
  }

  async decline() {
    if (!this.orderId) return;

    this.actionBusy = true;

    try {
      await firstValueFrom(this.orderService.declineOrder(this.orderId));

      // Bust + refetch details so UI stays consistent
      this.orderService.invalidateOrderDetails(this.orderId);
      const latest = await firstValueFrom(
        this.orderService.viewOrderDetails(this.orderId, true),
      );
      if (latest) {
        this.updateViewState(() => {
          this.details = latest;
        });
      }
    } catch (err: any) {
      console.error('Decline failed', err);
      alert(err?.message ?? 'Failed to decline order');
    } finally {
      this.updateViewState(() => {
        this.actionBusy = false;
      });
    }
  }

  startPrep() {
    this.applyAction(
      () => {
        this.orderService.startPreparing(this.orderId!);
        return of(void 0);
      },
      'cooking',
      'preparing',
    );
  }

  sendToDelivery() {
    this.applyAction(
      () => {
        this.orderService.markOutForDelivery(this.orderId!);
        return of(void 0);
      },
      'delivery',
      'delivery',
    );
  }

  complete() {
    this.applyAction(
      () => {
        this.orderService.markCompleted(this.orderId!);
        return of(void 0);
      },
      'completed',
      'completed',
    );
  }

  get totalItemsCount(): number {
    const list = this.details?.details as
      | Array<{ itemQuantity?: string | number }>
      | undefined;
    if (!Array.isArray(list)) return 0;
    return list.reduce((sum, it) => {
      const q =
        typeof it.itemQuantity === 'string'
          ? Number(it.itemQuantity)
          : (it.itemQuantity ?? 0);
      return sum + (isNaN(q) ? 0 : q);
    }, 0);
  }

  private async applyAction(
    run$: () => Observable<unknown>,
    backendStatus: string,
    localStatus: LocalStatus,
  ) {
    if (!this.orderId) return;
    if (this.actionBusy) return;

    this.actionBusy = true;

    // Optimistic patch so the header chip / CTAs switch instantly
    if (this.details)
      this.details = { ...this.details, orderStatus: backendStatus };
    this.liveStatus = localStatus;

    try {
      // ✅ ensure the observable actually runs
      await firstValueFrom(run$());

      // Bust + refetch details so header/status reflects server
      this.orderService.invalidateOrderDetails(this.orderId);
      const latest = await firstValueFrom(
        this.orderService.viewOrderDetails(this.orderId, true),
      );
      if (latest) {
        this.updateViewState(() => {
          this.details = latest;
        });
      }
    } catch (err: any) {
      console.error('Action failed', err);
      alert(err?.message ?? 'Failed to update order');
    } finally {
      this.updateViewState(() => {
        this.actionBusy = false;
      });
    }
  }

  get hasOrderComment(): boolean {
    const c = this.details?.comment;
    return typeof c === 'string' && c.trim().length > 0;
  }
  get orderComment(): string {
    return (this.details?.comment ?? '').trim();
  }

  get hasTenantComment(): boolean {
    const c = this.details?.tenantComment;
    return typeof c === 'string' && c.trim().length > 0;
  }
  get tenantComment(): string {
    return (this.details?.tenantComment ?? '').trim();
  }

  async copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // no-op; clipboard might be blocked in some envs
    }
  }

  async printReceipt() {
    if (!this.details) return;
    if (this.actionBusy) return;

    this.actionBusy = true;
    try {
      await this.printCurrentDetails();
    } catch (err: any) {
      console.error('Print failed', err);
      alert(
        'Print is only available on the connected tablet app.\n\n' +
          (err?.message ?? String(err)),
      );
    } finally {
      this.updateViewState(() => {
        this.actionBusy = false;
      });
    }
  }

  private async printCurrentDetails() {
    await SunmiPrinter.init();

    const lines = buildReceiptLines({
      tenantName: this.tenantName,
      companyNumber: this.companyNumber,
      details: this.details,
      width: 28,
    });

    const fallbackText = buildReceiptText({
      tenantName: this.tenantName,
      companyNumber: this.companyNumber,
      details: this.details,
      width: 28,
    });

    await printReceiptWithFallback({
      lines,
      fallbackText,
      fontSize: 28,
    });
  }
}
